// 啟用精靈（組織設定 › IT 監控 › 啟用，`/org/kmgr/enable`；SPEC #kinio-kms-onboarding「精靈」）。
// M1（k1-wbs M1-14）做 ⓪ passkey 檢查、① 接受資料處理協議、② 選家戶、⑤ 建第一把佈署權杖；
// ③ 授權台數、④ 安全預設、⑥ 收集器、⑦ 輪值表在 M2 才有對應功能，精靈不畫（UX-11），輪值表另在「輪值表」分頁。
// 每一步都可以略過；① 與 ② 一起送 POST /v1/orgs/{org}/kmgr/enable（只有組織 owner / org_admin，不經 P6）。
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { orgApi, type MyOrg } from '../../lib/planeApi';
import { listPasskeys, registerSignPasskey, passkeyErrorCode } from '../../lib/session';
import { passkeySupported } from '../../lib/webauthn';
import { SkeletonLines } from '../../components/Skeleton';
import { kmgrApi } from './api';
import { useOrgKmgrStatus } from './hooks';
import { brandName, errText, NS } from './parts';
import { DeployTokenForm, TokenOnce } from './DeployTokens';
import { requestSignature } from './signWindow';
import type { CreatedDeployToken } from './types';

const STEPS = [0, 1, 2, 5] as const;
type Step = (typeof STEPS)[number];
const STEP_MARK: Record<Step, string> = { 0: '⓪', 1: '①', 2: '②', 5: '⑤' };

export function EnableWizard({ org }: { org: MyOrg }) {
  const { t } = useTranslation(NS);
  const { config, session } = useApp();
  // 代建中、我是代建者：① 資料處理協議要等客戶（接手後的擁有者）接受，其他步驟照做（SPEC #kinio-kms-handover「代建期間」、D39）
  const builder = !!org.built_by_partner && !!session?.user?.user_id && org.built_by_user_id === session.user.user_id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const raw = Number(sp.get('step') ?? '0');
  const step: Step = (STEPS as readonly number[]).includes(raw) ? (raw as Step) : 0;
  const go = (s: Step) => {
    const n = new URLSearchParams(sp);
    n.set('step', String(s));
    setSp(n);
  };
  const status = useOrgKmgrStatus(org);
  const pk = useQuery({ queryKey: ['passkeys'], queryFn: () => listPasskeys(), retry: 0 });
  const homes = useQuery({ queryKey: ['org-homes', org.org_id], queryFn: () => orgApi.homes(org), staleTime: 60_000 });
  const [accepted, setAccepted] = useState(false);
  const [dpaVersion, setDpaVersion] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedDeployToken | null>(null);
  const [tokenHome, setTokenHome] = useState('');
  const admin = org.role === 'owner' || org.role === 'org_admin';
  if (!admin)
    return (
      <div className="card km-state" data-testid="km-wizard-denied">
        <h2>{t('wizard.denied')}</h2>
        <p>{t('wizard.denied_body', { org: org.display_name })}</p>
      </div>
    );
  if (status.isLoading || homes.isLoading) return <SkeletonLines n={5} label={t('common.loading')} />;
  if (status.isError || !status.data)
    return (
      <div className="km-bar unk" role="status">
        <span className="ic" aria-hidden>
          ⊘
        </span>
        <span className="txt">{t('wizard.status_unavailable', { detail: errText(t, status.error) })}</span>
        <button className="btn btn-sm" onClick={() => void status.refetch()}>
          {t('common.retry')}
        </button>
      </div>
    );
  const st = status.data;
  const version = dpaVersion ?? st.dpa.current_version;
  const dpaDone = st.dpa.accepted;
  const enabledIds = new Set(st.homes.filter((h) => h.kmgr_enabled).map((h) => h.home_id));
  const orgHomes = homes.data ?? [];
  const sel = picked ?? orgHomes.map((h) => h.home_id);
  // 只算在簽章網址註冊的 KMS 簽章 passkey（D35）；App 登入用的品牌 passkey 不能簽 IT 監控
  const signPasskeys = (pk.data ?? []).filter((p) => p.kms_sign);
  const hasPasskey = signPasskeys.length > 0;
  const done: Record<Step, boolean> = { 0: hasPasskey, 1: dpaDone, 2: enabledIds.size > 0, 5: !!created };
  const progress = STEPS.filter((s) => done[s]).length;
  const agent = t('agent_name', { brand: brandName(config.display_name) });
  const homeLabel = (id: string) => orgHomes.find((h) => h.home_id === id)?.display_name ?? id;

  const enable = async () => {
    setBusy(true);
    setErr(null);
    try {
      await kmgrApi.enable(org.api, org.org_id, builder ? { home_ids: sel } : { dpa_version: version, home_ids: sel });
      await qc.invalidateQueries({ queryKey: ['kmgr'] });
      go(5);
    } catch (e) {
      if (isKinioError(e) && e.code === 'INVALID_ARGUMENT' && typeof e.detail?.current === 'string') {
        // 協議剛更新：用新版本、回第 ① 步重讀
        setDpaVersion(e.detail.current as string);
        setAccepted(false);
        setNotice(t('wizard.dpa_updated', { v: e.detail.current }));
        go(1);
      } else setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const addPasskey = async () => {
    setBusy(true);
    setErr(null);
    try {
      const signUrl = config.sign_url;
      if (!signUrl) {
        setErr(t('wizard.passkey_no_sign'));
        return;
      }
      await registerSignPasskey(t('wizard.passkey_name'), (options) => requestSignature(signUrl, 'register', { options }));
      await pk.refetch();
    } catch (e) {
      const c = passkeyErrorCode(e);
      setErr(t('wizard.passkey_err', { code: c }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="km" data-testid="km-wizard" data-step={step}>
      <ol className="km-steps" aria-label={t('wizard.steps')}>
        {STEPS.map((s) => (
          <li key={s} className={done[s] ? 'done' : ''} aria-current={s === step ? 'step' : undefined}>
            {done[s] && s !== step ? <span aria-hidden>✓ </span> : null}
            {STEP_MARK[s]} {t(`wizard.step_${s}`)}
            {done[s] && <span className="km-sr">{t('wizard.done_sr')}</span>}
          </li>
        ))}
      </ol>
      {notice && (
        <div className="km-bar warn" role="status">
          <span className="ic" aria-hidden>
            ⚠
          </span>
          <span className="txt">{notice}</span>
        </div>
      )}
      {created ? (
        <TokenOnce token={created} api={org.api} homeLabel={homeLabel(created.home_id)} />
      ) : (
        <div className="km-wiz">
          <section className="card" aria-labelledby="km-wiz-h">
            <h2 id="km-wiz-h" style={{ fontSize: 16, marginTop: 0 }}>
              {STEP_MARK[step]} {t(`wizard.title_${step}`)}
            </h2>
            {step === 0 && (
              <div data-testid="km-step-passkey">
                {hasPasskey ? (
                  <p>
                    <span aria-hidden style={{ color: 'var(--km-ok)' }}>
                      ✓{' '}
                    </span>
                    {t('wizard.passkey_ok', { n: signPasskeys.length })}
                  </p>
                ) : (
                  <>
                    <p>{t('wizard.passkey_need')}</p>
                    <p className="muted km-sub">{t('wizard.passkey_readonly')}</p>
                    {passkeySupported() ? (
                      <button className="btn" disabled={busy} onClick={() => void addPasskey()}>
                        {t('wizard.passkey_add')}
                      </button>
                    ) : (
                      <p className="muted">{t('wizard.passkey_unsupported')}</p>
                    )}
                  </>
                )}
              </div>
            )}
            {step === 1 && (
              <div data-testid="km-step-dpa">
                <p className="muted km-sub">{t('wizard.dpa_version', { v: version })}</p>
                {!dpaDone && builder ? (
                  <div className="km-bar warn" role="status" data-testid="km-dpa-builder">
                    <span className="ic" aria-hidden>
                      ⚠
                    </span>
                    <span className="txt">{t('handover.dpa_builder')}</span>
                  </div>
                ) : dpaDone ? (
                  <p>
                    <span aria-hidden style={{ color: 'var(--km-ok)' }}>
                      ✓{' '}
                    </span>
                    {t('wizard.dpa_accepted', { v: st.dpa.accepted_version, time: st.dpa.accepted_at ? st.dpa.accepted_at.slice(0, 10) : '' })}
                  </p>
                ) : (
                  <>
                    <h3 style={{ fontSize: 15 }}>{t('wizard.dpa_summary')}</h3>
                    <ul>
                      {(st.dpa.summary?.length ? st.dpa.summary : [t('wizard.dpa_s1', { org: org.display_name }), t('wizard.dpa_s2'), t('wizard.dpa_s3')]).map((x, i) => (
                        <li key={i}>{x}</li>
                      ))}
                    </ul>
                    {st.dpa.full_text_url && (
                      <p>
                        <a href={st.dpa.full_text_url} target="_blank" rel="noreferrer">
                          {t('wizard.dpa_full')}
                        </a>
                      </p>
                    )}
                    <label className="km-check">
                      <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                      {t('wizard.dpa_accept', { org: org.display_name, v: version })}
                    </label>
                  </>
                )}
              </div>
            )}
            {step === 2 && (
              <div data-testid="km-step-homes">
                {!dpaDone && !accepted && !builder ? (
                  <div className="km-bar warn" role="status">
                    <span className="ic" aria-hidden>
                      ⚠
                    </span>
                    <span className="txt">{t('wizard.need_dpa')}</span>
                    <button className="btn btn-sm" onClick={() => go(1)}>
                      {t('wizard.to_dpa')}
                    </button>
                  </div>
                ) : null}
                <p>{t('wizard.homes_intro')}</p>
                {orgHomes.length === 0 ? (
                  <p className="muted">{t('wizard.homes_none')}</p>
                ) : (
                  <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
                    <legend className="km-sr">{t('wizard.step_2')}</legend>
                    {orgHomes.map((h) => (
                      <label key={h.home_id} className="km-check">
                        <input
                          type="checkbox"
                          checked={enabledIds.has(h.home_id) || sel.includes(h.home_id)}
                          disabled={enabledIds.has(h.home_id)}
                          onChange={(e) => setPicked(e.target.checked ? [...sel, h.home_id] : sel.filter((x) => x !== h.home_id))}
                        />
                        {h.display_name}
                        {enabledIds.has(h.home_id) && <span className="muted km-sub"> · {t('wizard.home_enabled')}</span>}
                      </label>
                    ))}
                  </fieldset>
                )}
              </div>
            )}
            {step === 5 && (
              <div data-testid="km-step-token">
                {enabledIds.size === 0 ? (
                  <div className="km-bar warn" role="status">
                    <span className="ic" aria-hidden>
                      ⚠
                    </span>
                    <span className="txt">{t('wizard.need_homes')}</span>
                    <button className="btn btn-sm" onClick={() => go(2)}>
                      {t('wizard.to_homes')}
                    </button>
                  </div>
                ) : (
                  <DeployTokenForm
                    key={tokenHome || [...enabledIds][0]}
                    api={org.api}
                    homes={[...enabledIds].map((id) => ({ home_id: id, label: `${org.display_name} › ${homeLabel(id)}` }))}
                    homeId={tokenHome || [...enabledIds][0]}
                    onHome={setTokenHome}
                    defaultName={t('token.default_name', { home: homeLabel(tokenHome || [...enabledIds][0]) })}
                    onCreated={(c) => (setCreated(c), void qc.invalidateQueries({ queryKey: ['kmgr'] }))}
                    extraActions={
                      <button type="button" className="btn" onClick={() => nav('/org/kmgr')}>
                        {t('wizard.skip_finish')}
                      </button>
                    }
                  />
                )}
              </div>
            )}
            {err && (
              <p className="error" role="alert">
                {err}
              </p>
            )}
          </section>
          <aside className="card" aria-labelledby="km-wiz-side-h">
            <h2 id="km-wiz-side-h" style={{ fontSize: 16, marginTop: 0 }}>
              {t('wizard.progress', { n: progress, total: STEPS.length })}
            </h2>
            <p className="muted km-sub">{t('wizard.progress_note')}</p>
            {step === 1 && !dpaDone && (
              <>
                <h3 style={{ fontSize: 15 }}>{t('wizard.before_dpa')}</h3>
                <p className="km-sub">{t('wizard.before_dpa_body', { agent })}</p>
              </>
            )}
            {step === 5 && <p className="km-sub">{t('wizard.token_side')}</p>}
          </aside>
        </div>
      )}
      {!created && step !== 5 && (
        <div className="km-dock">
          <Link className="btn" to="/org/kmgr">
            {t('wizard.save_leave')}
          </Link>
          {step > 0 && (
            <button className="btn" onClick={() => go(STEPS[STEPS.indexOf(step) - 1])}>
              {t('wizard.prev')}
            </button>
          )}
          <button className="btn" onClick={() => go(STEPS[STEPS.indexOf(step) + 1])}>
            {t('wizard.skip')}
          </button>
          {step === 2 ? (
            <button className="btn btn-primary" disabled={busy || (!dpaDone && !accepted && !builder) || sel.filter((x) => !enabledIds.has(x)).length === 0} onClick={() => void enable()}>
              {busy ? t('common.saving') : t('wizard.enable', { n: sel.filter((x) => !enabledIds.has(x)).length })}
            </button>
          ) : (
            <button className="btn btn-primary" disabled={step === 1 && !dpaDone && !accepted && !builder} onClick={() => go(STEPS[STEPS.indexOf(step) + 1])}>
              {t(`wizard.next_${step}`)}
            </button>
          )}
        </div>
      )}
      {created && (
        <div className="toolbar wrap">
          <Link className="btn" to="/org/kmgr">
            {t('wizard.finish')}
          </Link>
        </div>
      )}
    </div>
  );
}
